import { Link2 } from 'lucide-react';
import React, { useState } from 'react';
import { Modal, Stack, TextInput, Button } from '@mantine/core';
import { confirmDeviceAccessSafe } from '@services/deviceManager';
import { BaseActionButton, BaseActionButtonProps } from './BaseActionButton';
import { Device } from '@types';

export interface ConnectDeviceButtonProps
  extends Omit<BaseActionButtonProps, 'action'> {
  userId: string;
  onSuccess?: () => void;
  deviceCode: Device['code'];
}

export const ConnectDeviceButton: React.FC<ConnectDeviceButtonProps> = ({
  userId,
  onSuccess,
  onError,
  confirmBeforeAction = false,
  children,
  deviceCode,
  ...buttonProps
}) => {
  const [opened, setOpened] = useState(false);
  const [loginCode, setLoginCode] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    if (deviceCode === undefined) return;

    setIsConnecting(true);
    try {
      await confirmDeviceAccessSafe(loginCode, deviceCode, userId);
      setOpened(false);
      setLoginCode('');
      onSuccess?.();
    } catch (error) {
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Funzione per chiudere la modale e resettare il deviceCode
  const handleCloseModal = () => {
    setOpened(false);
    setLoginCode('');
  };

  const handleDeviceCodeChange = (value: string) => {
    const val = value
      .replace(/[^A-Z0-9]/gi, '')
      .toUpperCase()
      .slice(0, 4);
    setLoginCode(val);
  };

  return (
    <>
      <BaseActionButton
        action={async () => setOpened(true)}
        tooltip="Abbina il dispositivo alla piattaforma: l'abbinamento collega il dispositivo da connettere alla piattaforma."
        confirmBeforeAction={confirmBeforeAction}
        leftSection={<Link2 size={16} />}
        color='blue'
        variant='light'
        size='xs'
        {...buttonProps}
      >
        {children || 'Abbina'}
      </BaseActionButton>

      <Modal
        opened={opened}
        onClose={handleCloseModal}
        title='Esegui login dispositivo'
      >
        <Stack>
          <TextInput
            label='Inserisci il codice che vedi sul dispositivo'
            value={loginCode}
            onChange={e => {
              handleDeviceCodeChange(e.currentTarget.value);
            }}
            placeholder='Inserisci il codice di 4 cifre che vedi sul dispositivo'
            maxLength={4}
          />
          <Button
            onClick={handleConnect}
            disabled={!loginCode.trim()}
            loading={isConnecting}
            fullWidth
          >
            Abbina
          </Button>
        </Stack>
      </Modal>
    </>
  );
};
